import { SessionContext } from '@/context/SessionContext';
import { Anchor, Button, Checkbox, Group, Modal, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconLock, IconUser } from '@tabler/icons-react';
import { useContext, useEffect, useState } from 'react';
import RegistrationModal from './registration-modal';
import ForgotPasswordModal from './forgot-password-modal';

interface LoginModalProps {
	showModal: boolean;
	setShowModal: (show: boolean) => void;
}

const LoginModal: React.FC<LoginModalProps> = (props) => {
	const session = useContext(SessionContext);
	if (session === undefined) {
		throw new Error("Modals must be a child of sessionContext.");
	}

	const { showModal, setShowModal } = props;
	const [showRegistrationForm, setShowRegistrationForm] = useState(false);
	const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);

	// If the user is already signed in, then navigate out of the login modal
	useEffect(() => {
		if (session.user != null) {
			setShowModal(false);
		}
	}, [session.user]);

	const form = useForm({
		mode: 'uncontrolled',
		initialValues: {
			username: '',
			password: '',
			rememberMe: false,
		}
	});

	const handleSubmit = async ({ username, password, rememberMe }: typeof form.values) => {
		if (await session.login(username, password, rememberMe)) {
			setShowModal(false);
		}
	};

	return (
		<Modal
			opened={showModal}
			onClose={() => setShowModal(false)}
			title="Log In"
			centered
			size="md"
			styles={{
				title: {
					fontSize: 26,
					fontVariant: "all-small-caps"
				}
			}}
		>
			<RegistrationModal
				showModal={showRegistrationForm}
				setShowModal={setShowRegistrationForm}
			/>

			<ForgotPasswordModal
				showModal={showForgotPasswordModal}
				setShowModal={setShowForgotPasswordModal}
			/>

			<Stack>
				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Stack>
						<TextInput
							label={
								<Group>
									<IconUser size={20} />
									<Text>Username</Text>
								</Group>
							}
							disabled={form.submitting}
							{...form.getInputProps('username')}
							required
							withAsterisk={false}
						/>

						<PasswordInput
							label={
								<Group>
									<IconLock size={20} />
									<Text>Password</Text>
									<Text></Text>
								</Group>
							}
							disabled={form.submitting}
							{...form.getInputProps('password')}
							required
							withAsterisk={false}
						/>
						
						<Anchor
							component="button"
							type="button"
							onClick={() => {
								setShowForgotPasswordModal(true);
							}}
							size="s"
						>
							Forgot password?
						</Anchor>

						<Checkbox
							label="Remember Me"
							{...form.getInputProps('rememberMe', { type: 'checkbox' })}
						/>

						<Button
							type="submit"
							radius="xl"
							size='sm'
							loading={form.submitting}
							color="blue.7"
						>
							Log In
						</Button>
					</Stack>
				</form>

				<Stack
					align="center"
				>
					<Anchor
						component="button"
						type="button"
						onClick={() => {
							setShowRegistrationForm(true);
						}}
						size="s"
					>
						Don&apos;t have an account? Register here.
					</Anchor>
				</Stack>
			</Stack>
		</Modal>
	);
};

export default LoginModal;
